import {
  ApiProjectSettingKey,
  ApiService,
  ApiSimpleUTM,
  ApiSyncStatus,
  ApiSyncType,
} from "@incendium/api";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  capitalize,
  Divider,
  FormControlLabel,
  List,
  ListItemText,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { syncService } from "Apis";
import {
  StyledDrawerActions,
  StyledDrawerContainer,
} from "Components/UI/StyledDrawer";
import { useNotification, useSelectedProject } from "Hooks";
import { useExternalSyncs } from "Hooks/useExternalSyncs";
import { useSyncClients } from "Hooks/useSyncClients";
import produce from "immer";
import { ChangeEvent, Fragment, useCallback, useEffect, useState } from "react";
import { useConfirmationContext } from "Providers/ConfirmationProvider";
import { ISyncConfigComponentProps } from "../types";
import { AccordianChartBuilderSidebarBlock } from "features/chartLibrary";
import {
  bulkDeleteUTMs,
  bulkSaveUTMs,
  UtmGuide,
  UtmMappingList,
} from "features/utms";
import { useUTMs } from "Hooks/useUTMs";
import { compareUtms } from "features/utms/utils";
import ConnectButton from "../components/ConnectButton";
import { ProjectOptionsDrawerForm, saveProjectSetting } from "features/project";
import { useDispatch } from "react-redux";
import { fetchAllClientsAndProjectsThunk } from "Actions";
import SyncConnectAdAccount from "../components/SyncConnectAdAccount";
import { usePrevious, useUpdateEffect } from "react-use";
import SyncStyledListItem from "features/dataConnectors/components/SyncStyledListItem";
import UtmEntityList from "features/dataConnectors/components/UtmEntityList";

interface ISyncAccountConfigDrawerProps extends ISyncConfigComponentProps {
  provider: string;
  type: ApiSyncType;
  service: ApiService;
  connectDescription?: string | React.ReactNode;
  features?: (
    | "connect"
    | "setup"
    | "mapping"
    | "google-match-type"
    | "bing-match-type"
    | "ads-status"
  )[];
}

function SyncAccountConfigDrawer({
  oauthClient,
  syncs,
  provider,
  type,
  open,
  setOpen,
  service,
  connectDescription,
  features,
}: ISyncAccountConfigDrawerProps) {
  const { selectedProject, projectSettings, setProjectSettings } =
    useSelectedProject();
  const prevProjectSettings = usePrevious(projectSettings);
  const [projectSettingsDirty, setProjectSettingsDirty] = useState(false);
  const {
    getAccounts,
    accounts,
    accountsLoading,
    syncClients,
    setSyncClients,
  } = useSyncClients();
  const { externalSyncs, setExternalSyncs } = useExternalSyncs();
  const { utms, setUTMs, refetch } = useUTMs();
  const [originalUtms, setOriginalUtms] = useState<ApiSimpleUTM[] | null>(null);
  const { openConfirmation, closeConfirmation } = useConfirmationContext();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [error, setError] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (open && utms && !originalUtms) {
      setOriginalUtms(utms);
    } else if (!open) {
      setOriginalUtms(null);
      refetch();
    }
  }, [open, utms, refetch, originalUtms]);

  useEffect(() => {
    const fetchData = async (oauthClientId) => {
      try {
        await getAccounts(oauthClientId);
        setError(false);
      } catch (error) {
        setError(true);
      }
    };

    if (oauthClient?.id) {
      fetchData(oauthClient.id);
    }
  }, [oauthClient?.id, getAccounts]);

  const removeAccount = useCallback(
    async (id: number) => {
      const sync = externalSyncs.find((d) => d.id === id);
      openConfirmation({
        title: (
          <>
            <Typography variant="subtitle1">
              Are you sure you want to remove this Account?
            </Typography>
            <Typography variant="body2">
              Doing so shall remove all data associated with this account
            </Typography>
            <Typography variant="body2">
              This includes {sync?.campaignsCount} campaigns
            </Typography>
            <Typography variant="body2">
              and {sync?.clicksCount} clicks.
            </Typography>
          </>
        ),
        callback: async () => {
          try {
            await syncService.syncServiceDeleteSync({
              projectId: selectedProject?.id as number,
              syncId: id,
            });

            setExternalSyncs(
              produce(externalSyncs, (draft) => {
                const idx = draft.findIndex((d) => d.id === id);
                if (idx >= 0) {
                  draft.splice(idx, 1);
                }
              })
            );

            showSuccessNotification(`Account removed.`);
          } catch (error) {
            showErrorNotification(`Failed to remove account.`);
          }

          closeConfirmation();
        },
      });
    },
    [
      openConfirmation,
      closeConfirmation,
      selectedProject,
      showSuccessNotification,
      showErrorNotification,
      externalSyncs,
      setExternalSyncs,
    ]
  );

  const saveUTMs = useCallback(
    async (close: boolean) => {
      try {
        const { utmsToSave, utmsToDelete } = compareUtms(
          originalUtms || [],
          utms
        );
        if (utmsToSave && utmsToSave.length > 0) {
          await bulkSaveUTMs(selectedProject!.id!, utmsToSave);
        }
        if (utmsToDelete && utmsToDelete.length > 0) {
          await bulkDeleteUTMs(
            selectedProject!.id!,
            utmsToDelete.map((u) => u.id!)
          );
        }

        showSuccessNotification(`UTM Mapping updated`);
        if (close) {
          setOpen(false);
        }
      } catch (error) {
        showErrorNotification(
          `Internal Error updating UTM mapping, please try again`
        );
      }
    },
    [
      utms,
      originalUtms,
      showSuccessNotification,
      showErrorNotification,
      selectedProject,
      setOpen,
    ]
  );

  const saveProjectSettings = useCallback(
    (key: ApiProjectSettingKey) => async (e?: React.FormEvent) => {
      if (e) {
        e.preventDefault();
      }

      try {
        await saveProjectSetting(
          {
            key,
            value: projectSettings[key],
          },
          selectedProject!
        );

        showSuccessNotification(`Settings Saved`);
        dispatch(
          fetchAllClientsAndProjectsThunk({
            clientId: selectedProject!.clientId as number,
            projectId: selectedProject!.id as number,
          })
        );
      } catch (error) {
        showErrorNotification(
          `Internal Error saving Settings, please try again`
        );
      }
    },
    [
      dispatch,
      projectSettings,
      selectedProject,
      showSuccessNotification,
      showErrorNotification,
    ]
  );

  const onChangeProjectSettings = useCallback(
    (
      e: ChangeEvent<HTMLInputElement>,
      setting: ApiProjectSettingKey,
      checked?: boolean
    ) => {
      if (e.target.type === "checkbox") {
        setProjectSettings((settings) =>
          produce(settings, (draft) => {
            draft[setting] = checked ? "true" : "false";
          })
        );
      } else {
        setProjectSettings((settings) =>
          produce(settings, (draft) => {
            draft[setting] = e.target.value;
          })
        );
      }
      setProjectSettingsDirty(true);
    },
    [setProjectSettings]
  );

  const onProjectSettingChange = useCallback(
    (setting: ApiProjectSettingKey) => {
      if (
        !projectSettingsDirty ||
        (prevProjectSettings &&
          projectSettings[setting] === prevProjectSettings[setting])
      ) {
        return;
      }
      saveProjectSettings(setting)();
    },
    [
      projectSettingsDirty,
      projectSettings,
      saveProjectSettings,
      prevProjectSettings,
    ]
  );

  const onConnected = useCallback(() => {
    window.location.reload();
  }, []);

  useUpdateEffect(() => {
    onProjectSettingChange(ApiProjectSettingKey.USE_ECWID_PIXEL);
  }, [projectSettings[ApiProjectSettingKey.USE_ECWID_PIXEL]]);

  useUpdateEffect(() => {
    onProjectSettingChange(ApiProjectSettingKey.GOOGLE_ENABLE_MATCH_TYPE);
  }, [projectSettings[ApiProjectSettingKey.GOOGLE_ENABLE_MATCH_TYPE]]);

  useUpdateEffect(() => {
    onProjectSettingChange(ApiProjectSettingKey.BING_ENABLE_MATCH_TYPE);
  }, [projectSettings[ApiProjectSettingKey.BING_ENABLE_MATCH_TYPE]]);

  useUpdateEffect(() => {
    onProjectSettingChange(ApiProjectSettingKey.ENABLE_GOOGLE_SYNC_UTM);
  }, [projectSettings[ApiProjectSettingKey.ENABLE_GOOGLE_SYNC_UTM]]);

  return (
    <Box>
      {(!features || features.includes("connect")) && (
        <>
          <Box >
            <SyncConnectAdAccount
              oauthClient={oauthClient}
              syncs={syncs}
              service={service}
              type={type}
              provider={provider}
              error={error}
              accounts={accounts}
              accountsLoading={accountsLoading}
              project={selectedProject!}
              externalSyncs={externalSyncs}
              setExternalSyncs={setExternalSyncs}
              syncClients={syncClients}
              setSyncClients={setSyncClients}
              description={connectDescription}
            />


            {(syncs || []).length > 0 && (
              <>
                <Divider />
                <StyledDrawerContainer>
                  <Box>
                    <Typography variant="subtitle1">
                      {capitalize(provider)} Ads connections
                    </Typography>
                    <Typography variant="body2">
                      Select an account to remove, warning this will also remove
                      all associated data.
                    </Typography>
                    <List>
                      {(syncs || []).map((s) => (
                        <Fragment key={s.id}>
                          <SyncStyledListItem
                            disableGutters
                            error={s.status === ApiSyncStatus.ERROR}
                            secondaryAction={
                              <Stack spacing={1}>
                                {s.status === ApiSyncStatus.ERROR &&
                                  oauthClient && (
                                    <ConnectButton
                                      service={service}
                                      syncClients={syncClients}
                                      setSyncClients={setSyncClients}
                                      oauthClientId={s.oauthClientId}
                                      onConnected={onConnected}
                                    />
                                  )}
                                <Button
                                  size="extraSmall"
                                  variant="outlined"
                                  onClick={() => removeAccount(s.id as number)}
                                >
                                  Remove synced Account
                                </Button>
                              </Stack>
                            }
                          >
                            <ListItemText
                              primary={`${s.accountName} - ${s.account}`}
                              secondary={
                                s.status === ApiSyncStatus.ERROR ? (
                                  <Typography variant="subtitle2">
                                    This connection is no longer valid
                                    {oauthClient && ", please reconnect"}
                                  </Typography>
                                ) : (
                                  <>
                                    <Typography variant="body2">
                                      {s.client?.user?.name}
                                    </Typography>
                                    {type !== ApiSyncType.ECWID_API && (
                                      <Typography
                                        variant="body2"
                                        color={"secondary"}
                                      >
                                        clicks - {s.clicksCount}, campaigns -{" "}
                                        {s.campaignsCount}
                                      </Typography>
                                    )}
                                  </>
                                )
                              }
                            />
                          </SyncStyledListItem>
                        </Fragment>
                      ))}
                    </List>
                  </Box>
                  {type === ApiSyncType.ECWID_API && (
                    <>
                      <Typography variant="subtitle2" mb={1}>
                        Ecwid Settings
                      </Typography>
                      <Stack>
                        <FormControlLabel
                          componentsProps={{
                            typography: {
                              variant: "body1",
                            },
                          }}
                          control={
                            <Switch
                              checked={
                                projectSettings[
                                ApiProjectSettingKey.USE_ECWID_PIXEL
                                ] === "true"
                              }
                              onChange={(e, checked) => {
                                onChangeProjectSettings(
                                  e,
                                  ApiProjectSettingKey.USE_ECWID_PIXEL,
                                  checked
                                );
                              }}
                            />
                          }
                          label="Use Pixel"
                        />
                        <Typography variant="body2">
                          This will add Incendium's pixel to your Ecwid store, You
                          may want this setting off if you are using incendium
                          with datalayer.
                        </Typography>
                      </Stack>
                    </>
                  )}
                </StyledDrawerContainer>
              </>
            )}
            {type === ApiSyncType.FACEBOOK_ADS && (
              <ProjectOptionsDrawerForm
                onSubmit={saveProjectSettings(ApiProjectSettingKey.FB_PIXEL)}
                projectSettings={projectSettings}
                setProjectSettings={setProjectSettings}
                showSettings={[ApiProjectSettingKey.FB_PIXEL]}
                hideUTMS
                saveText="Save Pixel Settings"
              />
            )}
          </Box>

          <Divider />
        </>
      )}

      {(!features || features.includes("ads-status")) && (
        <AccordianChartBuilderSidebarBlock
          title="Your Ads & Tracking Statuses"
          subTitle={
            "View your ads and see if there are any which need UTM tags fixing"
          }
          defaultClosed
        >
          <Box sx={{ marginLeft: -3, width: `calc(100% + 48px)` }}>
            <UtmEntityList />
          </Box>
        </AccordianChartBuilderSidebarBlock>
      )}
      {(!features || features.includes("setup")) && (
        <AccordianChartBuilderSidebarBlock
          title="Suggested UTM Setup"
          subTitle={
            type === ApiSyncType.GOOGLE_ADS
              ? `To ensure accurate tracking and attribution of your campaigns, turn on Auto Tagging or add one of the following tracking templates.`
              : `To ensure accurate tracking and attribution of your campaigns, add one of the following tracking templates.`
          }
          defaultClosed
        >
          <Stack spacing={2}>
            {type === ApiSyncType.GOOGLE_ADS && (
              <Alert severity="info">
                <AlertTitle>Auto Tagging</AlertTitle>
                <Typography variant="body2" mb={2}>
                  We recommend enabling auto-tagging. Doing so will allow us to
                  add custom Incendium UTM parameters to your ad account.
                </Typography>
                <Box pl={0.5}>
                  <FormControlLabel
                    componentsProps={{
                      typography: {
                        variant: "body1",
                      },
                    }}
                    control={
                      <Switch
                        checked={
                          projectSettings[
                          ApiProjectSettingKey.ENABLE_GOOGLE_SYNC_UTM
                          ] === "true"
                        }
                        onChange={(e, checked) => {
                          onChangeProjectSettings(
                            e,
                            ApiProjectSettingKey.ENABLE_GOOGLE_SYNC_UTM,
                            checked
                          );
                        }}
                      />
                    }
                    label="Enable Auto Tagging"
                  />
                </Box>
              </Alert>
            )}
            <UtmGuide syncType={type} />
          </Stack>
        </AccordianChartBuilderSidebarBlock>
      )}
      {(!features || features.includes("google-match-type")) && (
        <AccordianChartBuilderSidebarBlock
          title="Use Match Type for Attribution"
          subTitle="Note turning on this setting can have negative impact to the initial click if you do not include match type in your utms."
          defaultClosed
        >
          <Stack spacing={1}>
            <FormControlLabel
              componentsProps={{
                typography: {
                  variant: "body1",
                },
              }}
              control={
                <Switch
                  checked={
                    projectSettings[
                    ApiProjectSettingKey.GOOGLE_ENABLE_MATCH_TYPE
                    ] === "true"
                  }
                  onChange={(e, checked) => {
                    onChangeProjectSettings(
                      e,
                      ApiProjectSettingKey.GOOGLE_ENABLE_MATCH_TYPE,
                      checked
                    );
                  }}
                />
              }
              label="Use Google Match Type"
            />
            {projectSettingsDirty &&
              projectSettings[ApiProjectSettingKey.GOOGLE_ENABLE_MATCH_TYPE] ===
              "true" && (
                <Alert severity="warning">
                  This may take a while to take effect.
                </Alert>
              )}
          </Stack>
        </AccordianChartBuilderSidebarBlock>
      )}
      {(!features || features.includes("bing-match-type")) && (
        <AccordianChartBuilderSidebarBlock
          title="Use Match Type for Attribution"
          subTitle="Note turning on this setting can have negative impact to the initial click if you do not include match type in your utms."
          defaultClosed
        >
          <Stack>
            <FormControlLabel
              componentsProps={{
                typography: {
                  variant: "body1",
                },
              }}
              control={
                <Switch
                  checked={
                    projectSettings[
                    ApiProjectSettingKey.BING_ENABLE_MATCH_TYPE
                    ] === "true"
                  }
                  onChange={(e, checked) => {
                    onChangeProjectSettings(
                      e,
                      ApiProjectSettingKey.BING_ENABLE_MATCH_TYPE,
                      checked
                    );
                  }}
                />
              }
              label="Use Bing Match Type"
            />
            {projectSettingsDirty &&
              projectSettings[ApiProjectSettingKey.BING_ENABLE_MATCH_TYPE] ===
              "true" && (
                <Alert severity="warning">
                  This may take a while to take effect.
                </Alert>
              )}
          </Stack>
        </AccordianChartBuilderSidebarBlock>
      )}
      {(!features || features.includes("mapping")) && (
        <>
          <AccordianChartBuilderSidebarBlock
            title="Custom UTM Mapping"
            subTitle="Customize UTM mappings for this provider by overriding default settings. This can be achieved by creating one or multiple catch-all configurations. Our system will search through each mapping until the correct one is found."
            defaultClosed
          >
            <UtmMappingList syncType={type} utms={utms} setUTMs={setUTMs} />
          </AccordianChartBuilderSidebarBlock>
          <StyledDrawerActions justifyContent={"flex-end"}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => saveUTMs(false)}
            >
              Save UTMs
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => saveUTMs(true)}
            >
              Save UTMs and Close
            </Button>
          </StyledDrawerActions>
        </>
      )}
    </Box>
  );
}

export default SyncAccountConfigDrawer;
